import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowDown, Gauge, Lock, Fingerprint, MessageSquareWarning, Cpu, Users,
  FileCheck2, Server, ShieldCheck, Award, ScrollText, KeyRound, Check, Zap,
} from "lucide-react";
import { Reveal, SectionHeading, Eyebrow } from "../components/common/Section";
import { Button } from "../components/ui/button";
import ArchitectureFlow from "../components/defence/ArchitectureFlow";
import UseCaseSelector from "../components/defence/UseCaseSelector";
import AdversarialDemo from "../components/defence/AdversarialDemo";
import ResponsibleAI from "../components/defence/ResponsibleAI";
import AssuranceExplorer from "../components/defence/AssuranceExplorer";
import DeploymentExplorer from "../components/defence/DeploymentExplorer";
import BriefingForm from "../components/defence/BriefingForm";
import { track } from "../lib/analytics";

const CHALLENGE_ITEMS = [
  "Sensitive information", "Model outputs", "User permissions", "AI-generated recommendations",
  "AI agents and tool actions", "Human approvals", "Incident escalation", "Data handling", "Evidence and accountability",
];

const EVIDENCE_ROWS = [
  ["Event", "Assistant asked to release restricted internal information"],
  ["Risk", "Sensitive-data extraction · Critical"],
  ["Policy", "Restricted information not disclosed outside authorised scope"],
  ["System action", "HOLD + ESCALATE"],
  ["Human reviewer", "Authorised reviewer (assigned)"],
  ["Decision", "Rejected — request out of scope"],
  ["Timestamp", "Recorded at time of interaction"],
  ["Outcome", "No disclosure · evidence retained"],
];

const OVERSIGHT_TIERS = [
  { level: "Low risk", action: "Continue", tone: "green" },
  { level: "Medium risk", action: "Warn / record", tone: "cyan" },
  { level: "High risk", action: "Hold", tone: "amber" },
  { level: "Critical", action: "Block + escalate", tone: "red" },
];

const toneMap = {
  green: "border-signal-green/40 bg-signal-green/[0.06] text-signal-green",
  cyan: "border-primary/40 bg-primary/[0.06] text-primary",
  amber: "border-signal-amber/40 bg-signal-amber/[0.06] text-signal-amber",
  red: "border-signal-red/40 bg-signal-red/[0.06] text-signal-red",
};

function FlowChips({ steps }) {
  return (
    <div className="mt-5 space-y-1.5">
      {steps.map((s, i) => (
        <div key={i}>
          <div className="rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2.5 text-sm text-foreground/90">{s}</div>
          {i < steps.length - 1 && <div className="flex justify-center py-0.5"><ArrowDown className="h-3.5 w-3.5 text-muted-foreground/40" /></div>}
        </div>
      ))}
    </div>
  );
}

export default function Defence() {
  useEffect(() => { document.title = "Defence & National Security | norse3"; }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-backdrop opacity-50" aria-hidden="true" />
        <div className="absolute inset-0 radial-fade" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-14 items-center">
            <div className="animate-fade-up">
              <Eyebrow>Defence & National Security</Eyebrow>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.3rem] font-bold leading-[1.05] text-foreground text-balance">
                Move faster with AI. Keep humans in control.
              </h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl">
                AI can create significant operational advantage, but sensitive environments require
                stronger control, assurance and accountability. norse3 provides an independent
                governance layer around AI interactions — applying organisation-defined policies,
                intercepting designated risks and creating reviewable evidence of what happened.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/defence#briefing" onClick={() => track("defence_hero_cta", { cta: "briefing" })} data-testid="defence-hero-briefing">
                    Request a Defence Briefing <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
                  <Link to="/defence#architecture" onClick={() => track("defence_hero_cta", { cta: "architecture" })}>Explore the Architecture</Link>
                </Button>
              </div>
              <p className="mt-6 text-xs text-muted-foreground/70 max-w-xl">
                norse3 helps organisations govern the AI they choose to deploy. It is not a weapons
                system and does not replace the underlying AI, model or agent.
              </p>
            </div>
            <div id="architecture" className="animate-fade-up scroll-mt-24 lg:pl-4" style={{ animationDelay: "120ms" }}>
              <ArchitectureFlow />
            </div>
          </div>
        </div>
      </section>

      {/* THE DEFENCE AI CHALLENGE */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="The defence AI challenge"
              title="AI adoption cannot come at the expense of control."
              subtitle="Defence organisations increasingly need to exploit commercial and specialist AI while maintaining appropriate governance over the things that matter most."
            />
          </Reveal>
          <Reveal className="mt-8" delay={80}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {CHALLENGE_ITEMS.map((c) => (
                <div key={c} className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-navy-900/60 px-4 py-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" /> <span className="text-sm text-foreground/90">{c}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal className="mt-8" delay={120}>
            <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8">
              <div className="grid sm:grid-cols-[1fr_auto_1fr] items-center gap-6">
                <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-6 text-center">
                  <Zap className="mx-auto h-6 w-6 text-primary" />
                  <p className="mt-3 text-lg font-bold text-foreground">Move at AI speed</p>
                  <p className="mt-1 text-sm text-muted-foreground">Adopt commercial and specialist AI quickly.</p>
                </div>
                <div className="flex items-center justify-center text-2xl font-bold text-muted-foreground/60">↔</div>
                <div className="rounded-xl border border-signal-amber/30 bg-signal-amber/[0.05] p-6 text-center">
                  <ShieldCheck className="mx-auto h-6 w-6 text-signal-amber" />
                  <p className="mt-3 text-lg font-bold text-foreground">Maintain control</p>
                  <p className="mt-1 text-sm text-muted-foreground">Keep policy, oversight and evidence in place.</p>
                </div>
              </div>
              <p className="mt-6 text-center text-base font-medium text-foreground">norse3 is designed to help organisations do both.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SIX BENEFITS */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Six defence benefits" title="Governance that keeps pace with adoption." /></Reveal>
          <div className="mt-10 grid gap-4 lg:grid-cols-3 sm:grid-cols-2">
            {/* 01 */}
            <Reveal>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="01" icon={Fingerprint} title="Protect sensitive information" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Reduce the risk of personnel unintentionally exposing sensitive information to AI systems.</p>
                <FlowChips steps={["User submits information", "Aegis detects designated sensitive content", "Policy applied", "Redact / Block / Escalate", "Event recorded"]} />
              </div>
            </Reveal>
            {/* 02 */}
            <Reveal delay={70}>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="02" icon={MessageSquareWarning} title="Govern AI outputs" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Inspect designated AI outputs before they reach users or downstream workflows.</p>
                <div className="mt-5 grid gap-2">
                  {["Unsupported claims", "Restricted information", "Policy violations", "Unsafe recommendations", "Unapproved content"].map((r) => (
                    <div key={r} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2 text-sm text-foreground/90"><span className="h-1.5 w-1.5 rounded-full bg-signal-amber" /> {r}</div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-muted-foreground/70">No control detects every risk. Coverage is configured to your policy.</p>
              </div>
            </Reveal>
            {/* 03 */}
            <Reveal delay={140}>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="03" icon={Cpu} title="Control AI agents" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">As AI moves from answering questions to taking actions, governance must move with it.</p>
                <FlowChips steps={["AI proposes an action", "norse3 evaluates policy", "Permitted → continue · otherwise → hold", "Human authorisation", "Approve / reject", "Evidence recorded"]} />
                <p className="mt-4 text-xs text-muted-foreground/70">e.g. accessing a restricted internal system, sending an external communication, or retrieving information outside an authorised scope.</p>
              </div>
            </Reveal>
            {/* 04 */}
            <Reveal>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="04" icon={Users} title="Human oversight" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Keep accountable people at the points that matter. Policies determine which interactions continue automatically and which require review.</p>
                <div className="mt-5 space-y-2">
                  {OVERSIGHT_TIERS.map((t) => (
                    <div key={t.level} className={`flex items-center justify-between rounded-lg border px-3.5 py-2.5 ${toneMap[t.tone]}`}>
                      <span className="text-sm font-medium text-foreground">{t.level}</span>
                      <span className="text-xs font-semibold">{t.action}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            {/* 05 */}
            <Reveal delay={70}>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="05" icon={FileCheck2} title="Reviewable evidence" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Know what happened, which policy applied and who made the final decision.</p>
                <div className="mt-5 rounded-xl border border-white/10 bg-navy-800/50 overflow-hidden">
                  {EVIDENCE_ROWS.map(([k, v], i) => (
                    <div key={k} className={`grid grid-cols-[92px_1fr] gap-3 px-3.5 py-2 text-xs ${i % 2 ? "bg-white/[0.015]" : ""}`}>
                      <span className="font-mono uppercase tracking-wide text-primary/70">{k}</span>
                      <span className="text-foreground/85">{v}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-muted-foreground/70">Synthetic Heimdall record. Illustrative only.</p>
              </div>
            </Reveal>
            {/* 06 */}
            <Reveal delay={140}>
              <div className="h-full rounded-lg border border-white/10 bg-navy-900/60 p-6">
                <BenefitHead n="06" icon={Server} title="Deployment control" />
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Deploy according to the sensitivity of the environment.</p>
                <div className="mt-5 space-y-2">
                  {[["Hosted", "Managed by norse3"], ["Private cloud", "Your cloud tenancy"], ["Customer-controlled", "Your environment"]].map(([n, d]) => (
                    <div key={n} className="flex items-center justify-between rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2.5">
                      <span className="text-sm font-medium text-foreground">{n}</span>
                      <span className="text-xs text-muted-foreground">{d}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-muted-foreground/70">Exact architecture, accreditation, network requirements and data residency require technical discovery. Air-gapped, classified or SECRET-level deployment is not claimed.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="Defence use cases" title="One control layer across defence AI adoption." /></Reveal>
          <Reveal className="mt-10" delay={80}><UseCaseSelector /></Reveal>
        </div>
      </section>

      {/* ADVERSARIAL AI */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Adversarial AI"
              title="AI systems will be tested by more than ordinary users."
              subtitle="Run a synthetic attack and watch it move through detection, policy, intervention, incident and review. These are demonstrations only and do not enable real attacks."
            />
          </Reveal>
          <Reveal className="mt-10" delay={80}><AdversarialDemo /></Reveal>
        </div>
      </section>

      {/* RESPONSIBLE AI */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Responsible AI"
              title="Governance that supports responsible adoption."
              subtitle="How norse3 controls map to relevant public frameworks and principles. Select a framework to see the principle, the operational question, the norse3 control and the evidence it creates."
            />
          </Reveal>
          <Reveal className="mt-10" delay={80}><ResponsibleAI /></Reveal>
        </div>
      </section>

      {/* ASSURANCE EXPLORER */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Defence AI assurance explorer"
              title="Assess an AI deployment."
              subtitle="Answer a few questions about an AI system and generate an illustrative control architecture showing where norse3 could sit. This is not a security accreditation or regulatory approval."
            />
          </Reveal>
          <Reveal className="mt-10" delay={80}><AssuranceExplorer /></Reveal>
        </div>
      </section>

      {/* DEPLOYMENT VISUAL */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Deployment"
              title="Deploy according to the environment."
              subtitle="norse3 sits as an independent control layer between your users and the approved AI services you deploy. Switch between deployment options to see how the boundary changes with sensitivity."
            />
          </Reveal>
          <Reveal className="mt-10" delay={80}><DeploymentExplorer /></Reveal>
        </div>
      </section>

      {/* PROCUREMENT / TRUST */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Procurement & assurance"
              title="Built for environments where assurance matters."
              subtitle="Diligence-ready information, showing only what is verified today."
            />
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Award, t: "ISO 9001", d: "Quality management — certified.", verified: true },
              { icon: Award, t: "ISO 42001", d: "AI management system — certified.", verified: true },
              { icon: ScrollText, t: "SOC 2", d: "Roadmap / readiness work in progress — not yet certified.", verified: false },
              { icon: Users, t: "Human oversight", d: "Escalation and final decisions stay with your people." },
              { icon: ShieldCheck, t: "Policy management", d: "Configure and version organisation-defined policies." },
              { icon: FileCheck2, t: "Audit records", d: "Reviewable evidence for each handled interaction." },
              { icon: Server, t: "Flexible deployment", d: "Hosted, private cloud or customer-controlled." },
              { icon: KeyRound, t: "Configurable access", d: "Role-based access to the console and records." },
              { icon: Lock, t: "Configurable retention & tenant separation", d: "Retention windows to policy; tenants not commingled by default." },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <div key={x.t} className="h-full rounded-xl border border-white/10 bg-navy-900/60 p-6">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    {x.verified === true && <span className="rounded-full border border-signal-green/40 bg-signal-green/[0.06] px-2 py-0.5 text-[0.6rem] font-semibold text-signal-green">VERIFIED</span>}
                    {x.verified === false && <span className="rounded-full border border-signal-amber/40 bg-signal-amber/[0.06] px-2 py-0.5 text-[0.6rem] font-semibold text-signal-amber">ROADMAP</span>}
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-foreground">{x.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-8">
            <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
              <Link to="/trust-centre" data-testid="defence-trust-cta">Visit Trust Centre <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* BRIEFING */}
      <section id="briefing" className="py-16 lg:py-20 scroll-mt-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 grid lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2">
            <Eyebrow>Defence sales journey</Eyebrow>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-foreground text-balance">Request a Defence & National Security Briefing.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Tell us a little about your organisation and area of interest, and we'll arrange an
              appropriate briefing with the norse3 team.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
              {["An independent view of where governance could sit around your AI", "How policy, oversight and evidence apply to your environment", "Deployment options against your sensitivity requirements"].map((x) => (
                <li key={x} className="flex items-start gap-2.5"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {x}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-3" delay={100}><BriefingForm /></Reveal>
        </div>
      </section>

      {/* FINAL POSITIONING */}
      <section className="border-t border-white/10 py-20 lg:py-24 bg-navy-950/40">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <div className="flex flex-wrap justify-center gap-2">
              {["Adopt AI faster", "Protect sensitive information", "Control what AI can do", "Maintain human accountability", "Create evidence", "Deploy to the environment"].map((x) => (
                <span key={x} className="rounded-full border border-white/12 bg-white/[0.03] px-3.5 py-1.5 text-sm text-foreground/85">{x}</span>
              ))}
            </div>
            <h2 className="mt-8 text-3xl sm:text-4xl font-bold text-foreground text-balance">AI capability will move quickly. Control must move with it.</h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/defence#briefing" data-testid="defence-final-briefing">Request a Defence Briefing <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
                <Link to="/platform">Explore norse3</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function BenefitHead({ n, icon: Icon, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
      <div>
        <span className="block font-mono text-xs text-primary/60">{n}</span>
        <h3 className="text-base font-semibold text-foreground leading-tight">{title}</h3>
      </div>
    </div>
  );
}
